import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { CheckIcon, ChevronDownIcon } from "lucide-react";
import type * as React from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/web/components/primitives/dropdown-menu";
import { cn } from "@/web/lib/styling";

export interface MultiSelectOption<TValue = string> {
  value: TValue;
  label: string;
  icon?: React.ReactNode;
}

interface MultiSelectProps<TValue = string> {
  options: MultiSelectOption<TValue>[];
  selectedValues: TValue[];
  /** Called when an individual item is toggled. */
  onToggle?: (value: TValue, checked: boolean) => void;
  /** Called with the full array of selected values. */
  onSelectionChange?: (values: TValue[]) => void;
  placeholder: string;
  className?: string;
  contentClassName?: string;
  align?: "start" | "center" | "end";
}

export function MultiSelect<TValue = string>({
  options,
  selectedValues,
  onToggle,
  onSelectionChange,
  placeholder,
  className,
  contentClassName,
  align = "start",
}: MultiSelectProps<TValue>) {
  const selectedCount = selectedValues.length;

  const handleCheckedChange = (value: TValue, checked: boolean) => {
    if (onToggle) {
      onToggle(value, checked);
    } else if (onSelectionChange) {
      if (checked) {
        onSelectionChange([...selectedValues, value]);
      } else {
        onSelectionChange(selectedValues.filter((v) => v !== value));
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "font-withus-sans inline-flex h-11 min-w-[140px] shrink-0 cursor-pointer items-center justify-between gap-2 rounded-none border px-3 text-base transition-shadow outline-none",
            "hover:shadow-[0_2px_8px_rgba(0,0,0,0.08)]",
            "focus-visible:ring-2 focus-visible:ring-black/20",
            selectedCount > 0
              ? "border-withus-blue bg-white text-withus-blue"
              : "border-border bg-white text-withus-dark-blue",
            className,
          )}
        >
          <span>{placeholder}</span>
          {selectedCount > 0 ? (
            <span className="flex min-w-6 items-center justify-center bg-withus-blue/5 px-1.5 py-0.5 text-sm font-medium text-withus-blue">
              {selectedCount}
            </span>
          ) : null}
          <ChevronDownIcon className="size-4 shrink-0 opacity-70" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          "max-h-72 w-80 overflow-x-hidden overflow-y-auto rounded-none border border-b-[rgba(7,15,34,0.1)] bg-white p-5 shadow-[0px_0px_14px_0px_rgba(0,0,0,0.25)]",
          contentClassName,
        )}
        align={align}
      >
        <div className="flex flex-col gap-4">
          {options.map((option) => {
            const checked = selectedValues.includes(option.value);
            return (
              <DropdownMenuPrimitive.Item
                key={String(option.value)}
                onSelect={(e) => {
                  e.preventDefault();
                  handleCheckedChange(option.value, !checked);
                }}
                className="flex cursor-pointer items-center justify-between gap-2 rounded-none outline-none select-none"
              >
                <span className="flex items-center gap-[6px]">
                  {option.icon && (
                    <span className="flex size-[18px] shrink-0 items-center justify-center text-[#070f22]">
                      {option.icon}
                    </span>
                  )}
                  <span className="text-[16px] font-medium text-[#070f22]">{option.label}</span>
                </span>
                <span
                  className={cn(
                    "relative size-5 shrink-0 border-[1.5px] border-[rgba(7,15,34,0.1)]",
                    checked && "bg-[#2f2f87]",
                  )}
                >
                  {checked && (
                    <CheckIcon className="absolute top-1/2 left-1/2 size-[14px] -translate-x-1/2 -translate-y-1/2 text-white" />
                  )}
                </span>
              </DropdownMenuPrimitive.Item>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
